/** MUST be first: populates process.env.REACT_APP_* under `vite dev` before any module-scope env reads. */
import './craEnvBridge'
import './utils/axiosSessionBootstrap'
/** Side effect: registers global axios interceptors (session / deleted-account handling) before any callApi/store traffic. */
import './common/api'
import React from 'react'
import ReactDOM from 'react-dom/client'

import './index.css'
import './styles/mobile-readability.css'
import 'tailwindcss/tailwind.css'
// Shared modal parts (chevron button, tabs, cards, frame, close) used across
// many lazy-loaded modals. Loaded globally: Vite's per-route CSS splitting
// hoists CSS shared across lazy chunks into just ONE chunk, so a modal opened
// from a route that isn't that chunk would render unstyled. Global import
// guarantees these styles are always present.
import './components/Modal/restorePlate.css'
// Same reasoning for the LandPageModal enter/exit motion — LandPageModal is
// used across many lazy routes, so its animation CSS must be globally present.
import './components/Modal/LandPageModal.css'
import { initI18n } from './i18n/config'
import App from './App'
import { prefetchGameNews } from './common/gameNewsWarm'
import reportWebVitals from './reportWebVitals'
import { MaintenancePage } from './pages/MaintenancePage'
import {
  initPwaInstallPromptCapture,
  initStandaloneLandscapeOrientation,
} from './utils/pwaInstallFlow'
import { setBlobCacheSwRegistrationPromise } from './utils/assetWarmup'

initPwaInstallPromptCapture()
initStandaloneLandscapeOrientation()

const isMaintenanceActive = false

// Single SW registration — done eagerly (not deferred to the `load` event) so
// the SW can intercept navigation requests as soon as possible.  The browser
// deduplicates registrations for the same URL, so a single call is sufficient.
//
// The promise is handed to `assetWarmup` so the startup cache audit can await the registration
// instead of racing it. Without that, a reload could run the audit before the worker existed,
// conclude the cache was empty, and re-download the whole ~82 MB gate that was already stored.
if (!isMaintenanceActive && 'serviceWorker' in navigator) {
  setBlobCacheSwRegistrationPromise(
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }),
  )
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
const wheel = document.getElementById('root') as HTMLElement
wheel.addEventListener(
  'wheel',
  (event) => {
    if (event.ctrlKey) {
      event.preventDefault()
    }
  },
  true,
)
void initI18n().then(() => {
  root.render(
    <React.StrictMode>
      {isMaintenanceActive ? (
        <MaintenancePage />
      ) : (
        <App />
      )}
    </React.StrictMode>,
  )
})

// Warm public news in the background so NewsModal does not wait on first open.
if (!isMaintenanceActive) {
  setTimeout(() => {
    void prefetchGameNews()
  }, 0)
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
